import { motion } from "framer-motion";
import { TriangleAlert, Power, Inbox } from "lucide-react";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";

const FAILURES = [
    {
        icon: TriangleAlert,
        title: "It hallucinates.",
        code: "ERR-01",
    },
    {
        icon: Power,
        title: "It breaks the process.",
        code: "ERR-02",
    },
    {
        icon: Inbox,
        title: "It drops an unstructured mess into your team's lap.",
        code: "ERR-03",
    },
];

export default function Problem() {
    return (
        <section id="problem" className="relative py-24 sm:py-28" data-testid="section-problem-ai-illusion">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHead
                    index="01"
                    label="The Problem"
                    title={
                        <>
                            The “Set It and Forget It”{" "}
                            <span className="text-blaze">AI Illusion</span>
                        </>
                    }
                />

                <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
                    {/* narrative */}
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="text-base leading-relaxed text-slate-300 sm:text-lg">
                                Mid-size companies are rapidly deploying AI and automation to
                                scale operations. But AI is only efficient at the predictable
                                80%.
                            </p>
                            <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                                When an automated workflow encounters an exception it wasn't
                                trained for, it doesn't just pause. It hallucinates, breaks the
                                process, or drops an unstructured mess directly into your team's
                                lap. Employees are forced to stop their strategic work to
                                reverse-engineer the machine's mistake.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <blockquote className="mt-10 border-l-2 border-blaze pl-6">
                                <p className="font-display text-xl font-semibold uppercase leading-snug tracking-wide text-white sm:text-2xl">
                                    The workflow isn't failing because the technology is flawed.
                                    It is failing because the handoff from the machine back to
                                    the human was{" "}
                                    <span className="text-blaze">never engineered</span>.
                                </p>
                            </blockquote>
                        </Reveal>
                    </div>

                    {/* 80/20 visualization + failure modes */}
                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <div className="border border-line bg-panel/60 p-6 sm:p-8">
                                <div className="flex flex-wrap items-baseline justify-between gap-2 font-mono text-[11px] uppercase tracking-[0.22em]">
                                    <span className="text-signal">80% — Predictable, automated</span>
                                    <span className="text-blaze">20% — Exceptions, unhandled</span>
                                </div>
                                <div className="mt-4 flex h-4 w-full overflow-hidden bg-ink">
                                    <motion.div
                                        className="h-full bg-signal/70"
                                        initial={{ width: 0 }}
                                        whileInView={{ width: "80%" }}
                                        viewport={{ once: true, amount: 0.6 }}
                                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                                    />
                                    <motion.div
                                        className="h-full bg-blaze"
                                        initial={{ width: 0 }}
                                        whileInView={{ width: "20%" }}
                                        viewport={{ once: true, amount: 0.6 }}
                                        transition={{ duration: 1.2, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                                    />
                                </div>
                                <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Where your workflow actually breaks
                                </div>
                            </div>
                        </Reveal>

                        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {FAILURES.map((f, i) => (
                                <Reveal key={f.code} delay={0.1 + i * 0.1} className="h-full">
                                    <div className="group flex h-full flex-col border border-line bg-panel/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-blaze/50 hover:bg-panelhi">
                                        <div className="flex items-center justify-between">
                                            <f.icon className="h-6 w-6 text-blaze" strokeWidth={1.75} />
                                            <span className="font-mono text-[10px] tracking-[0.2em] text-slate-600">
                                                {f.code}
                                            </span>
                                        </div>
                                        <p className="mt-6 text-sm font-semibold leading-snug text-slate-200">
                                            {f.title}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={0.35}>
                            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                // Employees stop strategic work to reverse-engineer the
                                machine's mistake.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
